import { FocusRegion, VideoSegmentMultiFocusDesignSystemConfig } from "../VideoSegmentMultiFocus";
import { DefaultFocusVideoSegment } from "./FocusTypes/DefaultFocusVideoSegment";
import { TwoSplitFocusVideoSegment } from "./FocusTypes/TwoSplitFocusVideoSegment";
import { ThreeSplitFocusVideoSegment } from "./FocusTypes/ThreeSplitFocusVideoSegment";
import { ScreenshareFocusVideoSegment } from "./FocusTypes/ScreenshareFocusVideoSegment";
import { FourSplitFocusVideoSegment } from "./FocusTypes/FourSplitFocusVideoSegment";
import { GameplayFocusVideoSegment } from "./FocusTypes/GameplayFocusVideoSegment";
import { SquareFocusVideoSegment } from "./FocusTypes/SquareFocusVideoSegment";
import { LandscapeFocusVideoSegment } from "./FocusTypes/LandscapeFocusVideoSegment";
import { PortraitFocusVideoSegment } from "./FocusTypes/PortraitFocusVideoSegment";

export const onVideoFrame = (frame: CanvasImageSource, focusRegions: FocusRegion<any>[], canvasRefs: React.RefObject<HTMLCanvasElement>[]) => {
    focusRegions.forEach((region, index) => {
        const canvas = canvasRefs[index].current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        // Get frame dimensions based on type
        const frameWidth = frame instanceof HTMLVideoElement ? frame.videoWidth : frame instanceof HTMLImageElement ? frame.naturalWidth : 0;
        const frameHeight = frame instanceof HTMLVideoElement ? frame.videoHeight : frame instanceof HTMLImageElement ? frame.naturalHeight : 0;

        if (frameWidth === 0 || frameHeight === 0) return;

        // Convert region percentage to pixel values based on video frame size
        const xPixelValue = Math.floor(region.x * frameWidth);
        const yPixelValue = Math.floor(region.y * frameHeight);
        const widthPixelValue = Math.floor(region.width * frameWidth);
        const heightPixelValue = Math.floor(region.height * frameHeight);

        // Skip invalid/zero-sized regions to avoid division-by-zero
        if (widthPixelValue <= 0 || heightPixelValue <= 0) {
            context.clearRect(0, 0, canvas.width, canvas.height);
            return;
        }

        // Calculate aspect ratios
        const sourceAspectRatio = widthPixelValue / heightPixelValue;
        const canvasAspectRatio = canvas.width / canvas.height;

        // Calculate destination rectangle to preserve aspect ratio
        let destX = 0;
        let destY = 0;
        let destWidth = canvas.width;
        let destHeight = canvas.height;

        if (sourceAspectRatio > canvasAspectRatio) {
            // Source is wider than canvas - letterbox top and bottom
            destHeight = canvas.width / sourceAspectRatio;
            destY = (canvas.height - destHeight) / 2;
        } else if (sourceAspectRatio < canvasAspectRatio) {
            // Source is taller than canvas - pillarbox left and right
            destWidth = canvas.height * sourceAspectRatio;
            destX = (canvas.width - destWidth) / 2;
        }

        // Clear canvas and fill with black (for letterboxing/pillarboxing)
        context.fillStyle = '#000000';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the cropped region with aspect ratio preserved
        context.drawImage(
            frame,
            xPixelValue,
            yPixelValue,
            widthPixelValue,
            heightPixelValue,
            destX,
            destY,
            destWidth,
            destHeight
        );
    });
};

export const DefaultVideoSegmentMultiFocus: React.FC<VideoSegmentMultiFocusDesignSystemConfig> = ({ startTimestampMS, endTimestampMS, videoURL, spacing: padding, borderRadius, border, borderStyle, framing, designSystem, audioURL, animation, inCloudRenderingEnvironment }) => {
    if (framing.name === 'default') {
        return (
            <DefaultFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderRadius={borderRadius}
                border={border}
                borderStyle={borderStyle}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        );
    }

    if (framing.name === '2-split') {
        return (
            <TwoSplitFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderRadius={borderRadius}
                border={border}
                borderStyle={borderStyle}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        );
    }

    if (framing.name === '3-split') {
        return (
            <ThreeSplitFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderRadius={borderRadius}
                border={border}
                borderStyle={borderStyle}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        );
    }

    if (framing.name === '4-split') {
        return (
            <FourSplitFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderRadius={borderRadius}
                border={border}
                borderStyle={borderStyle}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        );
    }

    if (framing.name === 'screenshare') {
        return (
            <ScreenshareFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderStyle={borderStyle}
                borderRadius={borderRadius}
                border={border}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        );
    }

    if (framing.name === 'gameplay') {
        return (
            <GameplayFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderStyle={borderStyle}
                borderRadius={borderRadius}
                border={border}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        );
    }

    if (framing.name === 'square') {
        return (
            <SquareFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderStyle={borderStyle}
                borderRadius={borderRadius}
                border={border}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        )
    }

    if (framing.name === 'landscape') {
        return (
            <LandscapeFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderStyle={borderStyle}
                borderRadius={borderRadius}
                border={border}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        )
    }

    if (framing.name === 'portrait') {
        return (
            <PortraitFocusVideoSegment
                startTimestampMS={startTimestampMS}
                endTimestampMS={endTimestampMS}
                videoURL={videoURL}
                spacing={padding}
                borderStyle={borderStyle}
                borderRadius={borderRadius}
                border={border}
                framing={framing}
                designSystem={designSystem}
                audioURL={audioURL}
                animation={animation}
                inCloudRenderingEnvironment={inCloudRenderingEnvironment}
            />
        )
    }
};  